/**
 * @fileOverview Helpers for writing a tour: a step factory per route, and the checks a tour has to
 * pass. See docs/view-help.md.
 */

import type { ViewHelpAnchor, ViewHelpStep, ViewHelpTour } from "@/services/viewHelp/types"

/** Everything about a step but the four fields every step has. */
export type StepFields = Omit<ViewHelpStep, "key" | "route" | "anchor" | "icon">

/**
 * A factory for the steps read on one route. `shared` carries what every step there has in common —
 * typically the feature-set and permission gates of the view — and a step's own `fields` win over it.
 */
export function stepsOn(route: string, shared: StepFields = {}) {
  return (key: string, anchor: ViewHelpAnchor, icon: string, fields: StepFields = {}): ViewHelpStep => ({
    key,
    route,
    anchor,
    icon,
    ...shared,
    ...fields,
  })
}

/**
 * What is wrong with a tour, as messages; empty when nothing is.
 *
 * The invariants the rest of the feature reads as given, and none of them expressible in the type: a
 * step key is an i18n key, so it has to be unique inside its tour; a step's route has to be one the
 * trigger appears on, or the step is unreachable; actions change the page, so they must belong to a
 * scene whose exit changes it back; a scene is one stretch of steps, because leaving it runs its
 * exit and coming back to it later would re-enter it; a tab is pressed with `openTab`, never
 * `click`, so a tab that is already open is not pressed a second time in front of the reader; a
 * toggle is never undone with the click that set it; and
 * example pages cover the frame, so a step showing them has no anchor.
 */
export function tourProblems(tour: ViewHelpTour): string[] {
  const problems: string[] = []
  const sceneKeys = new Set((tour.scenes ?? []).map(scene => scene.key))
  const closedScenes = new Set<string>()
  const seen = new Set<string>()
  let previousScene: string | null = null
  let reachedRelated = false
  const closedSections = new Set<string>()
  let previousSection: string | null = null
  const relatedRoutes = tour.related ?? []
  // A section is related when its key is a related view, or any of its steps is on one.
  const relatedSections = new Set(
    tour.steps.filter(step => relatedRoutes.includes(step.route)).map(step => step.section ?? step.route)
  )
  for (const route of relatedRoutes) relatedSections.add(route)

  for (const step of tour.steps) {
    if (seen.has(step.key)) problems.push(`duplicate step key "${step.key}" in tour "${tour.key}"`)
    seen.add(step.key)
    const section = step.section ?? step.route
    const related = relatedSections.has(section)
    if (!tour.routes.includes(step.route) && !relatedRoutes.includes(step.route)) {
      problems.push(`step "${step.key}" is on route "${step.route}", which tour "${tour.key}" does not list`)
    }
    // The timeline draws the related views after the feature's own, so the steps have to come in that
    // order too — or the reader would be carried out to a settings page and back in again.
    if (related) reachedRelated = true
    else if (reachedRelated) problems.push(`step "${step.key}" comes after a related view's steps`)

    // The timeline draws a section as one bar, so its steps have to be one stretch.
    if (section !== previousSection && closedSections.has(section)) {
      problems.push(`section "${section}" is split — its steps must be consecutive`)
    }
    if (previousSection && previousSection !== section) closedSections.add(previousSection)
    previousSection = section

    if (step.actions && !step.scene) problems.push(`step "${step.key}" has actions but no scene`)
    if (step.images?.length && step.anchor !== null) {
      problems.push(`step "${step.key}" shows example pages, so its anchor must be null`)
    }
    if (step.scene && !sceneKeys.has(step.scene))
      problems.push(`step "${step.key}" names unknown scene "${step.scene}"`)
    if (step.scene && step.scene !== previousScene && closedScenes.has(step.scene)) {
      problems.push(`scene "${step.scene}" is split — its steps must be consecutive`)
    }
    if (previousScene && previousScene !== step.scene) closedScenes.add(previousScene)
    previousScene = step.scene ?? null
  }
  // Tabs are anchored `tab-<value>` or `<feature>-tab-<value>`, which is what this reads a tab by.
  const actions = [
    ...tour.steps.flatMap(step => step.actions ?? []),
    ...(tour.scenes ?? []).flatMap(scene => [...scene.enter, ...scene.exit]),
  ]
  for (const action of actions) {
    if (action.do === "click" && /(^|-)tab-/.test(action.target)) {
      problems.push(`"${action.target}" is pressed with click — use openTab, which skips a tab already open`)
    }
  }
  // A board's Heute as a scene's way in is `showToday`: a click pressed it again on a board that was
  // already on today, every time a scene was entered straight from the one before.
  for (const action of (tour.scenes ?? []).flatMap(scene => [...scene.enter, ...scene.exit])) {
    if (action.do === "click" && action.target === "planning-today") {
      problems.push(`a scene presses "planning-today" with click — use showToday, which skips a board already on today`)
    }
  }
  // A drag has no way back, so a scene that drags must say so and be replayed on a fresh page.
  for (const scene of tour.scenes ?? []) {
    const drags = [...scene.enter, ...tour.steps.filter(step => step.scene === scene.key).flatMap(step => step.actions ?? [])].some(
      action => action.do === "drag"
    )
    if (drags && !scene.irreversible) {
      problems.push(`scene "${scene.key}" drags, so it must be marked irreversible`)
    }
  }
  // A toggle undone with the same click it was switched on with. A scene is left even when its `enter`
  // or a step's own press was overtaken before it happened — and then that click switches *on* what was
  // never switched on, and every step after it builds on the wrong state.
  for (const scene of tour.scenes ?? []) {
    const pressed = new Set(
      [...scene.enter, ...tour.steps.filter(step => step.scene === scene.key).flatMap(step => step.actions ?? [])]
        .filter(action => action.do === "click")
        .map(action => (action as { target: string }).target)
    )
    for (const action of scene.exit) {
      if (action.do === "click" && pressed.has(action.target)) {
        problems.push(
          `scene "${scene.key}" undoes "${action.target}" with the click that set it — use expand/collapse or check/uncheck`
        )
      }
    }
  }
  const listed = (route: string) => tour.routes.includes(route) || (tour.related ?? []).includes(route)
  for (const [route, way] of Object.entries(tour.ways ?? {})) {
    if (!listed(route)) problems.push(`a way leads to route "${route}", which tour "${tour.key}" does not list`)
    for (const from of Array.isArray(way) ? [] : Object.keys(way.from)) {
      if (!listed(from)) problems.push(`a way to "${route}" starts on route "${from}", which tour "${tour.key}" does not list`)
    }
  }
  return problems
}

/**
 * What is wrong across tours: a route that is the trigger's route in two of them. `findTourForRoute`
 * would only ever offer the first, and the second tour's steps there would never play.
 */
export function catalogProblems(tours: ViewHelpTour[]): string[] {
  const owner = new Map<string, string>()
  const problems: string[] = []
  for (const tour of tours) {
    for (const route of tour.routes) {
      const other = owner.get(route)
      if (other) problems.push(`route "${route}" is in the routes of both tour "${other}" and tour "${tour.key}"`)
      else owner.set(route, tour.key)
    }
  }
  return problems
}
